/**
 * Helpers for the API-driven report pull page:
 *   - buildCurlSnippet: renders a copy-ready curl example for POST /pull.
 *   - validateDateRange: guards the date picker inputs + error messaging.
 *   - buildWarehouseSnippet: curl one-liner tweaked for Snowflake / BigQuery
 *     external-stage ingestion.
 *
 * Pure functions — no React, no fetch. Unit-tested directly to keep the
 * page component thin.
 */

export interface DateRangeInput {
	start: string;
	end: string;
}

export type DateRangeError =
	| { ok: true; start: string; end: string }
	| { ok: false; message: string };

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export function validateDateRange(input: DateRangeInput): DateRangeError {
	const { start, end } = input;
	if (!start || !end) {
		return { ok: false, message: "Pick both start and end dates." };
	}
	if (!ISO_DATE.test(start) || !ISO_DATE.test(end)) {
		return { ok: false, message: "Dates must be YYYY-MM-DD." };
	}
	const startMs = Date.parse(`${start}T00:00:00Z`);
	const endMs = Date.parse(`${end}T00:00:00Z`);
	if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
		return { ok: false, message: "Invalid calendar date." };
	}
	if (endMs < startMs) {
		return { ok: false, message: "End date must be on or after start date." };
	}
	const spanDays = Math.floor((endMs - startMs) / 86_400_000) + 1;
	if (spanDays > 366) {
		return { ok: false, message: "Range is capped at 366 days." };
	}
	return { ok: true, start, end };
}

export interface CurlInput {
	baseUrl: string;
	tenantId: string;
	pullId: string;
	reportType: string;
	start: string;
	end: string;
	pageSize: number;
}

export function buildCurlSnippet(input: CurlInput): string {
	const base = sanitizeBase(input.baseUrl);
	const body = JSON.stringify(
		{
			pull_id: input.pullId,
			report_type: input.reportType,
			date_range: { start: input.start, end: input.end },
			page_size: clampInt(input.pageSize, 1, 500, 100),
		},
		null,
		2,
	);
	return [
		`curl -X POST '${base}/api/v1/api-driven-report-pull-for-custom-bi-dashboards/pull' \\`,
		`  -H 'content-type: application/json' \\`,
		`  -H 'x-tenant-id: ${escapeHeader(input.tenantId)}' \\`,
		`  -d '${body.replace(/'/g, "'\\''")}'`,
	].join("\n");
}

export type Warehouse = "snowflake" | "bigquery";

export function buildWarehouseSnippet(warehouse: Warehouse, input: CurlInput): string {
	const base = sanitizeBase(input.baseUrl);
	const listUrl = `${base}/api/v1/api-driven-report-pull-for-custom-bi-dashboards?page_size=${clampInt(input.pageSize, 1, 500, 100)}`;
	if (warehouse === "snowflake") {
		return [
			`-- Snowflake external function / stage (via Storage Integration)`,
			`-- Step 1: stage the report JSON (run in your shell):`,
			`curl -H 'x-tenant-id: ${escapeHeader(input.tenantId)}' '${listUrl}' \\`,
			`  > /tmp/${input.pullId}.json`,
			`-- Step 2: load to raw table:`,
			`COPY INTO raw.bi_report_pull FROM @my_stage/${input.pullId}.json`,
			`  FILE_FORMAT = (TYPE = JSON STRIP_OUTER_ARRAY = FALSE);`,
		].join("\n");
	}
	return [
		`# BigQuery bq load (service account auth assumed)`,
		`curl -H 'x-tenant-id: ${escapeHeader(input.tenantId)}' '${listUrl}' \\`,
		`  | jq -c '.data[]' > /tmp/${input.pullId}.ndjson`,
		`bq load --source_format=NEWLINE_DELIMITED_JSON \\`,
		`  project:dataset.bi_report_pull \\`,
		`  /tmp/${input.pullId}.ndjson`,
	].join("\n");
}

function sanitizeBase(raw: string): string {
	const trimmed = raw.replace(/\/+$/, "");
	return trimmed || "https://api.example.com";
}

function escapeHeader(v: string): string {
	return v.replace(/['"\r\n]/g, "");
}

function clampInt(n: number | undefined, lo: number, hi: number, fallback: number): number {
	if (!Number.isFinite(n)) return fallback;
	const v = Math.floor(n as number);
	if (v < lo) return lo;
	if (v > hi) return hi;
	return v;
}

export const RESPONSE_SCHEMA = {
	POST_pull: {
		summary: "Create or idempotently return a report-pull job.",
		request: {
			pull_id: "string (client-supplied, unique per tenant)",
			report_type: "string",
			date_range: { start: "YYYY-MM-DD", end: "YYYY-MM-DD" },
			page_size: "integer (1..500, default 100)",
		},
		response_201_or_200: {
			id: "integer",
			pull_id: "string",
			report_type: "string",
			date_range: { start: "YYYY-MM-DD", end: "YYYY-MM-DD" },
			cursor: "string | null",
			page_size: "integer",
			requested_at: "unix seconds",
			delivered_at: "unix seconds | null",
			row_count: "integer",
			status: "pending | delivered | failed",
		},
	},
	GET_list: {
		summary: "Paginate existing pulls (cursor, tenant-scoped).",
		query: {
			page_size: "1..500",
			cursor: "next id from previous response",
		},
		response: {
			data: "Array<Pull>",
			paging: {
				cursor: "string | null",
				page_size: "integer",
				has_more: "boolean",
			},
		},
	},
} as const;
